import * as React from 'react';
import classnames from 'classnames';
import { isNil, snakeCase } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';

import './mention-card-metrics.scss';

import type {
  ChannelTypes,
  MentionCardIcon,
  MentionCardMetricType,
  MentionCardMetricsDetailsByType,
  MentionCardMetrics as MentionCardMetricsType,
  MentionCardViewType
} from 'types';

applyTranslations(require('./mention-card-metrics.i18n.json'));

type Props = {
  metrics: MentionCardMetricsType,
  channel: ChannelTypes,
  viewType: MentionCardViewType
};

class MentionCardMetrics extends React.PureComponent<Props> {
  renderIcon(iconName: MentionCardIcon): React.ReactNode {
    return (
      <SVGDynamicIcon
        name={iconName}
        width={16}
        height={16}
        customColor="grey"
      />
    );
  }

  renderMetric = (metricType: MentionCardMetricType): JSX.Element => {
    const { metrics, channel } = this.props;
    const values = metrics.values || {};
    const detail = metrics.details[metricType] as MentionCardMetricsDetailsByType;
    const metric = detail?.modifier(values[metricType]);
    const value = `${metric}${detail.mark || ''}`;
    const tooltipLabel = __t(`mention_card_metrics.label_${snakeCase(metricType)}`);

    const itemClasses = classnames('mention-card-metrics__item', {
      [`mention-card-metrics__item--channel-${channel}`]: Boolean(channel)
    });

    return (
      <div
        title={`${tooltipLabel}: ${value}`}
        className={itemClasses}
      >
        <div className="mention-card-metrics__icon">
          {this.renderIcon(detail.icon)}
        </div>
        <div className="mention-card-metrics__value">
          {value}
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { metrics, channel, viewType } = this.props;

    if (isNil(metrics)) {
      return null;
    }

    const containerClasses = classnames('mention-card-metrics', {
      [`mention-card-metrics--channel-${channel}`]: Boolean(channel),
      [`mention-card-metrics--view-type-${viewType}`]: Boolean(viewType)
    });

    const metricClasses = classnames('mention-card-metrics__metric', {
      [`mention-card-metrics__metric--channel-${channel}`]: Boolean(channel)
    });

    return (
      <div
        className={containerClasses}
        data-qa="mention-card-metrics"
      >
        {
          Object.keys(metrics.values)
            .filter(field => field !== 'dislikes')
            .map(metricType => (
              <div
                key={`mention-card-metrics--${metricType}`}
                className={metricClasses}
              >
                {this.renderMetric(metricType as MentionCardMetricType)}
              </div>
            ))
        }
      </div>
    );
  }
}

export default MentionCardMetrics;
